import { Button, Divider } from "@justworkshr/milo-core";
import styles from "./EditProfile.module.css";
import { useState } from "react";
import { ActionFooter } from "@justworkshr/milo-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  EmergencyContactInput,
  EorEmployeeProfileInput,
  GetEoREmployeeProfileQuery,
  GetEoRMemberByUuidQuery,
  InternationalAddressInput,
  PhoneNumberInput,
} from "types/generated/operations";
import { Form } from "@justworkshr/milo-form";
import { Formik } from "formik";
import { PHONE_TYPES } from "pages/employer-of-record/onboarding/contact-info/PhoneNumbersFragment";
import { groupBy, mapValues, matches, merge, omit } from "lodash";
import moment from "moment";
import { useApolloClient } from "@apollo/client";
import CancelEditModal from "./components/CancelEditModal";
import {
  AutoFormField,
  FieldTypes,
  generateFormValidations,
  INFORMATION_TYPES,
  InformationType,
} from "pages/employer-of-record/components/AutoForm";
import CollapsibleCard from "./components/CollapsibleCard";
import { ProfileField } from "pages/employer-of-record/configs/types";
import {
  initialValues,
  useFields,
  PROFILE_FIELDS,
  ProfileFieldData,
  ProfileFieldName,
} from "pages/employer-of-record/configs";
import useIsAdmin from "../../hooks/useIsAdmin";
import { useGetFeatureFlag } from "lib/launch-darkly";

const divideAfter = [
  PROFILE_FIELDS.dateOfBirth.name,
  PROFILE_FIELDS.pronouns.name,
  PROFILE_FIELDS.homeEmail.name,
  PROFILE_FIELDS.homeAddress.name,
];

const subtitlesBefore = {
  [PROFILE_FIELDS.emergencyContactFirstName.name]: "Emergency contact",
};

const cardTitles: Record<InformationType, string> = {
  identification: "Identification",
  taxId: "Identifier number",
  address: "Home & mailing address",
  benefits: "Benefits",
  cartao: "Cartão de Cidadão",
  contact: "Contact Information",
  contract: "Contract details",
  education: "Education",
  family: "Family",
  infonavitFonacot: "Infonavit & Fonacot Loans",
  job: "Job Information",
  pension: "Pensions",
  rg: "Carteira de identidade Nacional",
};

type Props = {
  profile: GetEoREmployeeProfileQuery["eorEmployeeProfile"];
  memberData: GetEoRMemberByUuidQuery["eorMember"];
  onSubmit: (values: EorEmployeeProfileInput) => void;
  onError: (message: string) => void;
  clearError: () => void;
  linkToProfile: string;
};

function encodeParams(queryParams: Record<string, string | boolean | number>) {
  return Object.entries(queryParams).map(
    ([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
  );
}

function EditableProfile({
  profile,
  memberData,
  onSubmit,
  onError,
  clearError,
  linkToProfile,
}: Props) {
  const client = useApolloClient();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getFeatureFlag } = useGetFeatureFlag();
  const [open, setOpen] = useState(
    Object.fromEntries(
      INFORMATION_TYPES.map((informationType) => [
        informationType,
        window.location.hash === `#${informationType}`,
      ])
    ) as Record<InformationType, boolean>
  );
  const [isModalOpen, setIsModalOpen] = useState(false); // State to manage modal visibility
  const { isAdmin } = useIsAdmin();
  const scenario =
    isAdmin &&
    !memberData.accountCreated &&
    getFeatureFlag("release-eor-resend-invite-and-edit-email")
      ? "preOnboardingEdit"
      : "editProfile";

  let fields = useFields({ scenario });
  if (!getFeatureFlag("release-eor-honored-start-date")) {
    fields = fields.filter(
      (field) => field.name !== PROFILE_FIELDS.honoredStartDate.name
    );
  }
  const groupedFields: Partial<Record<InformationType, ProfileField[]>> =
    groupBy(fields, ({ informationType }) => informationType);

  const workCountry = profile.employment?.workCountry ?? undefined;

  if (!workCountry) {
    return <div>Something went wrong</div>;
  }

  function toggleCollapsibleSection(sectionName: keyof typeof open) {
    return () =>
      setOpen({
        ...open,
        [sectionName]: !open[sectionName],
      });
  }

  function navigateToProfile(queryParams?: Parameters<typeof encodeParams>[0]) {
    const path =
      linkToProfile + (queryParams ? `?${encodeParams(queryParams)}` : "");
    // force cache to refresh user nav with updated information if name was changed
    if (!isAdmin) {
      client.refetchQueries({ include: ["GetUserMenu"] });
    }
    navigate(path);
  }

  function handleCancelClick() {
    setIsModalOpen(true); // Open the modal
  }

  function handleModalClose() {
    setIsModalOpen(false); // Close the modal
  }

  // Generic profileSection handling - used by all scenarios
  const buildGenericProfileSections = (values: Partial<ProfileFieldData>) => {
    // Group fields that have an explicit profileSection set
    const profileSectionGroupedFields = groupBy(
      fields.filter((field) => field.profileSection),
      ({ profileSection }) => profileSection!
    );

    const profileSectionValues = mapValues(
      profileSectionGroupedFields,
      (fields) =>
        Object.fromEntries(
          fields.map(({ name, serialize }) => {
            const value = (values as Record<string, string>)[name];
            const serializedValue = serialize ? serialize(value) : value;
            return [name, serializedValue];
          })
        )
    );

    // Remove redacted values from profile section values
    for (const [groupName, groupValues] of Object.entries(
      profileSectionValues
    )) {
      for (const [name, value] of Object.entries(groupValues)) {
        if (typeof value === "string" && value.includes("•")) {
          delete profileSectionValues[groupName][name];
        }
      }
    }

    return profileSectionValues;
  };

  async function onFormSubmit(values: Partial<ProfileFieldData>) {
    try {
      clearError();

      // Start with generic profileSection handling for all scenarios
      let submissionData = buildGenericProfileSections(values);

      // Add editProfile-specific custom logic
      if (scenario === "editProfile") {
        const homeAddress = omit(values.homeAddress, [
          "__typename",
          "type",
        ]) as InternationalAddressInput;

        const mailingAddress = omit(values.mailingAddress, [
          "__typename",
          "type",
        ]) as InternationalAddressInput;

        // Define custom additions for editProfile scenario
        const customAdditions = {
          personalInfo: {
            dateOfBirth: moment(values.dateOfBirth).format("YYYY-MM-DD"),
            emergencyContact: {
              firstName: values.emergencyContactFirstName,
              lastName: values.emergencyContactLastName,
              relationship: values.emergencyContactRelationship,
              phoneNumber: values.emergencyContactPhoneNumber!.value,
            } as EmergencyContactInput,
          },
          contactDetails: {
            homePhoneNumber: values.phoneNumbers?.find(
              matches({ type: PHONE_TYPES.home })
            ) as PhoneNumberInput,
            workPhoneNumber: values.phoneNumbers?.find(
              matches({ type: PHONE_TYPES.work })
            ) as PhoneNumberInput,
            mobilePhoneNumber: values.phoneNumbers?.find(
              matches({ type: PHONE_TYPES.mobile })
            ) as PhoneNumberInput,
            homeAddress: homeAddress,
            mailingAddress: values.sameAddress ? homeAddress : mailingAddress,
          },
        };

        // Merge custom additions with generic profile sections
        submissionData = merge(submissionData, customAdditions);
      }

      try {
        await onSubmit(submissionData);

        const preferredName = values.preferredFirstName || undefined;
        const memberName = preferredName ?? values.firstName ?? "the member";

        const updatedAlertText = `The changes you made to ${
          isAdmin ? `${memberName}'s` : "your"
        } profile have been saved.`;

        navigateToProfile({ updated: updatedAlertText });
      } catch (error) {
        onError(t("An unknown error occurred. Please try again later."));
      }
    } catch (outerError) {
      onError(t("An unknown error occurred. Please try again later."));
    }
  }

  const formSchema = generateFormValidations(fields, t);
  const sectionOrdering: InformationType[] = [
    "identification",
    "family",
    "education",
    "address",
    "contact", //TODO: Same address
    "job",
    "contract",
    "cartao",
  ];

  const homeAddressField = fields.find(
    (field) => field.name === PROFILE_FIELDS.homeAddress.name
  ) as ProfileField<FieldTypes.address, undefined> | undefined;

  if (homeAddressField) {
    homeAddressField.supportedCountries = [workCountry];
  }

  return (
    <>
      {isModalOpen && (
        <CancelEditModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          navigateBack={() => navigate(linkToProfile)}
        />
      )}
      <Formik
        initialValues={initialValues(fields, profile)}
        onSubmit={onFormSubmit}
        validationSchema={formSchema}
      >
        {(formikProps) => {
          return (
            <Form onSubmit={formikProps.handleSubmit}>
              <div className={styles.collapsesContainer}>
                {sectionOrdering.map((section) => {
                  const fields = groupedFields[section];
                  if (!fields?.length) return null;
                  return (
                    <EditProfileSection
                      title={cardTitles[section]}
                      fields={fields}
                      open={open[section]}
                      onClick={toggleCollapsibleSection(section)}
                      fieldStyles={{
                        dateOfBirth: styles.dateOfBirth,
                        sameAddress: styles.sameAddress,
                      }}
                    />
                  );
                })}

                <ActionFooter
                  actions={
                    <>
                      <Button
                        onClick={handleCancelClick}
                        disabled={formikProps.isSubmitting}
                        color="brand"
                        variant="ghost"
                      >
                        {t("Cancel")}
                      </Button>
                      <Button
                        disabled={formikProps.isSubmitting}
                        loading={formikProps.isSubmitting}
                        type="submit"
                      >
                        {t("Update")}
                      </Button>
                    </>
                  }
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
}

function EditProfileSection({
  title,
  fields,
  open,
  onClick,
  fieldStyles,
}: {
  title: string;
  fields: Array<ProfileField>;
  open: boolean;
  onClick: () => void;
  fieldStyles?: Partial<Record<ProfileFieldName, string>>;
}) {
  return (
    <CollapsibleCard open={open} title={title} onClick={onClick}>
      {fields.map((field, index) => {
        let component = <AutoFormField key={field.name} {...field} />;

        if (fieldStyles?.[field.name as ProfileFieldName]) {
          component = <div className={styles[field.name]}>{component}</div>;
        }

        if (subtitlesBefore[field.name]) {
          component = (
            <>
              <div className={styles.title}>{subtitlesBefore[field.name]}</div>
              {component}
            </>
          );
        }

        if (divideAfter.includes(field.name) && index !== fields.length - 1) {
          return (
            <>
              {component}
              <Divider />
            </>
          );
        }

        return component;
      })}
    </CollapsibleCard>
  );
}

export default EditableProfile;
